import React from 'react';
import {
  Button,
  Card,
  Drawer,
  DrawerContent,
  ScrollArea,
} from '@city-sage/aurum-avis-labs-components';
import { useQuery } from '@tanstack/react-query';
import { useRepository } from '@city-sage/aurum-avis-labs-components';
import { useTranslation } from 'react-i18next';
import {
  Navigation,
  MessageCircle,
  MapPin as MapPinIcon,
} from 'lucide-react';
import { Stop, Itinerary, Post, PostPostableTypeEnum } from '@city-sage/core-client';
import { useNavigate } from '@tanstack/react-router';
import { useNavigationModalHandler } from '../../lib/hooks/useNavigation';
import { NavigationProviderModal } from '../actions/NavigationProviderModal';
import { TripMap } from '../TripMap';
import { AggregatedStopProjection } from './types';
import { ItineraryCard } from '../ItineraryCard';
import { Avatar } from '../Avatar';
import { CommentButton } from '../actions/CommentButton';
import { ReportButton } from '../actions/ReportButton';
import { ImageGallery } from '../ImageGallery';

interface AggregatedStopPreviewSheetProps {
  aggregatedStop: AggregatedStopProjection | null;
  isOpen: boolean;
  onClose: () => void;
}

export const AggregatedStopPreviewSheet: React.FC<AggregatedStopPreviewSheetProps> = ({
  aggregatedStop,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { api } = useRepository();
  const navigation = useNavigationModalHandler();
  const navigate = useNavigate();

  // Fetch all stops for this aggregated location (for map display)
  const { data: stops = [] } = useQuery({
    queryKey: ['aggregated-stops', aggregatedStop?.stopIds],
    queryFn: async () => {
      if (!aggregatedStop?.stopIds || aggregatedStop.stopIds.length === 0) return [];
      
      // Fetch all stops in parallel
      const stopPromises = aggregatedStop.stopIds.map(stopId =>
        api.findOneStop({ id: stopId }).catch(() => null)
      );
      
      const results = await Promise.all(stopPromises);
      return results.filter((stop): stop is Stop => stop !== null);
    },
    enabled: !!aggregatedStop?.stopIds && aggregatedStop.stopIds.length > 0,
  });

  // Fetch full itineraries for ItineraryCard component
  const { data: fullItineraries = [] } = useQuery({
    queryKey: ['aggregated-full-itineraries', aggregatedStop?.itineraryIds],
    queryFn: async () => {
      if (!aggregatedStop?.itineraryIds || aggregatedStop.itineraryIds.length === 0) return [];
      
      // Fetch full itineraries in parallel
      const itineraryPromises = aggregatedStop.itineraryIds.map(itineraryId =>
        api.findOneItinerary({ id: itineraryId }).catch(() => null)
      );
      
      const results = await Promise.all(itineraryPromises);
      return results.filter((itinerary): itinerary is Itinerary => itinerary !== null);
    },
    enabled: !!aggregatedStop?.itineraryIds && aggregatedStop.itineraryIds.length > 0,
  });

  // Fetch comments from all stops at this location
  const { data: allComments = [] } = useQuery({
    queryKey: ['aggregated-place-comments', aggregatedStop?.stopIds],
    queryFn: async () => {
      if (!aggregatedStop?.stopIds || aggregatedStop.stopIds.length === 0) return [];
      
      // Fetch comments for all stops in parallel
      const commentPromises = aggregatedStop.stopIds.map(stopId =>
        api.findAllPost({
          filter: JSON.stringify({
            postableType: PostPostableTypeEnum.Stop,
            postableId: stopId
          }),
          limit: 1000
        }).catch(() => [])
      );
      
      const results = await Promise.all(commentPromises);
      // Flatten and filter for comments with text, sort by date
      const comments = results
        .flat()
        .filter((post: Post) => post.text && post.text.trim().length > 0)
        .sort((a: Post, b: Post) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      
      return comments;
    },
    enabled: !!aggregatedStop?.stopIds && aggregatedStop.stopIds.length > 0,
  });

  // Collect unique images from stops
  const placeImages = React.useMemo(() => {
    const imageMap = new Map<string, { url: string; image?: any; title?: string }>();
    
    stops.forEach(stop => {
      if (stop.imageUrl) {
        // Use imageUrl as key to avoid duplicates
        if (!imageMap.has(stop.imageUrl)) {
          imageMap.set(stop.imageUrl, {
            url: stop.imageUrl,
            image: stop.image,
            title: stop.title || stop.place?.name
          });
        }
      }
    });
    
    return Array.from(imageMap.values());
  }, [stops]);

  if (!aggregatedStop) return null;

  // Get location name from first stop's place name, or use full address
  const placeName = stops.length > 0 && stops[0]?.place?.name 
    ? stops[0].place.name 
    : null;
  const fullAddress = aggregatedStop.location.address?.fullAddress || 'Location';
  
  const itineraryCount = fullItineraries.length;
  const commentCount = allComments.length;

  // Handle navigation directions
  const handleNavigate = () => {
    if (aggregatedStop?.location) {
      const navTitle = placeName || fullAddress;
      navigation.navigate({
        latitude: aggregatedStop.location.latitude,
        longitude: aggregatedStop.location.longitude,
        title: navTitle,
        address: fullAddress,
      });
    }
  };

  // Navigate to an itinerary page
  const handleItineraryClick = (itineraryId: string) => {
    navigate({
      to: '/itineraries/$itineraryId',
      params: { itineraryId },
    });
    onClose();
  };

  return (
    <>
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="h-[95dvh] flex flex-col overflow-hidden">
          <ScrollArea className="flex-1 mt-4 w-full min-w-0 overflow-x-hidden mx-auto max-w-6xl">
            {/* Header Section */}
            <div className="px-4 md:px-10 py-6 border-b border-border">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPinIcon className="h-4 w-4" />
                      <span>Featured in {itineraryCount} {itineraryCount === 1 ? 'itinerary' : 'itineraries'}</span>
                      {commentCount > 0 && (
                        <>
                          <span className="mx-2">•</span>
                          <span>{commentCount} {commentCount === 1 ? 'comment' : 'comments'}</span>
                        </>
                      )}
                    </div>
                    {placeName ? (
                      <>
                        <h1 className="text-2xl md:text-3xl font-bold leading-tight">
                          {placeName}
                        </h1>
                        <p className="text-muted-foreground text-sm md:text-base">
                          {fullAddress}
                        </p>
                      </>
                    ) : (
                      <h1 className="text-2xl md:text-3xl font-bold leading-tight">
                          {fullAddress}
                        </h1>
                    )}
                  </div>
                </div>

              </div>
            </div>

            <div className="px-4 md:px-10 py-8 space-y-8 w-full min-w-0 max-w-full overflow-x-hidden">
              {/* Image Carousel Section */}
              {placeImages.length > 0 && (
                <section className="border-b border-border pb-8">
                  <ImageGallery
                    imageUrls={placeImages.map(img => img.url)}
                    images={placeImages.map(img => img.image)}
                    titles={placeImages.map(img => img.title)}
                    aspectRatio="aspect-[16/9]"
                    showCounter={true}
                    showArrows={true}
                    className="rounded-lg overflow-hidden"
                  />
                </section>
              )}

              {/* Map Section */}
              {aggregatedStop.location && stops.length > 0 && (
                <section className="border-b border-border pb-8">
                  <div className="mb-6">
                    <h2 className="text-2xl md:text-3xl font-light mb-2">Location</h2>
                    <p className="text-foreground/60">
                      View this location on the map
                    </p>
                  </div>
                  <div className="relative aspect-[1/1] md:aspect-[16/9] min-h-[400px] md:h-auto w-full overflow-hidden bg-muted rounded-sm">
                    <TripMap
                      stops={stops.length > 0 ? [stops[0]] : []}
                      days={[]}
                      legs={[]}
                      className="h-full"
                      interactive={true}
                      hideDayBadge={true}
                    />
                  </div>
                </section>
              )}

              {/* Itineraries Section - Main Content */}
              {fullItineraries.length > 0 && (
                <section>
                  <div className="mb-6">
                    <h2 className="text-2xl md:text-3xl font-light mb-2">Is Part Of Itineraries</h2>
                    <p className="text-foreground/60">
                      This location is featured in {itineraryCount} {itineraryCount === 1 ? 'itinerary' : 'itineraries'}
                    </p>
                  </div>
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {fullItineraries.map((itinerary) => (
                      <ItineraryCard
                        key={itinerary.id}
                        itinerary={itinerary}
                        link={`/itineraries/${itinerary.id}`}
                        showStateBadge={false}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Comments Section - Social Aspect */}
              {allComments.length > 0 && (
                <section>
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-2xl md:text-3xl font-light">What Travelers Say</h2>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MessageCircle className="h-4 w-4" />
                        <span>{commentCount} {commentCount === 1 ? 'comment' : 'comments'}</span>
                      </div>
                    </div>
                    <p className="text-foreground/60">
                      Comments from travelers who visited this location
                    </p>
                  </div>
                  <div className="space-y-4">
                    {allComments.slice(0, 10).map((comment: Post) => (
                      <Card key={comment.id} className="p-4">
                        <div className="flex gap-3">
                          {comment.account && (
                            <Avatar 
                              account={comment.account} 
                              className="h-10 w-10 flex-shrink-0"
                              textClassName="text-xs"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-sm">
                                {comment.account?.userName || 'Anonymous'}
                              </span>
                              {comment.createdAt && (
                                <span className="text-xs text-muted-foreground">
                                  {new Date(comment.createdAt).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-foreground/90 leading-relaxed">
                              {comment.text}
                            </p>
                              <div className="mt-2 flex items-center gap-2">
                              {comment.postableType === PostPostableTypeEnum.Stop && comment.postableId && (
                                <CommentButton
                                  postableType="stop"
                                  postableId={comment.postableId}
                                />
                              )}
                              <ReportButton
                                contentId={comment.id}
                                contentType="comment"
                                theme="light"
                              />
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </ScrollArea>

          {/* Footer Actions */}
          <div className="p-4 md:p-6 bg-background border-t border-border">
            <div className="space-y-3">
              <Button
                onClick={handleNavigate}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <Navigation className="w-4 h-4 mr-2" />
                {t('itinerary.stopPage.getDirections')}
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Navigation Provider Modal */}
      <NavigationProviderModal
        isOpen={navigation.isModalOpen}
        onClose={() => navigation.setIsModalOpen(false)}
        onSelect={navigation.handleProviderChoice}
        destinationName={placeName || fullAddress}
      />
    </>
  );
};

